import { CommonModule } from '@angular/common';
import { Component, Inject } from '@angular/core';
import { Router } from '@angular/router';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { ButtonModule } from 'primeng/button';
import { TableModule } from 'primeng/table';
import { TabsModule } from 'primeng/tabs';
import { TagModule } from 'primeng/tag';
import { Store } from '@ngrx/store';
import { take } from 'rxjs/operators';

import { getCurrentProject } from '@completion/selectors';
import { State } from '@completion/reducers';
import { GlobalSearchResponse, GlobalSearchResult, PunchService, SigningAction, TagService } from '@completion/services';
import { Project } from '@completion/models';
import { PunchCreateEditMode } from '@completion/enums';
import { PunchDialogComponent } from '../../shared/punch-dialog/punch-dialog.component';

@Component({
  selector: 'app-global-search-result-dialog',
  standalone: true,
  imports: [CommonModule, ButtonModule, TableModule, TabsModule, TagModule],
  templateUrl: './global-search-result-dialog.component.html',
  styleUrls: ['./global-search-result-dialog.component.scss'],
})
export class GlobalSearchResultDialogComponent {
  signingActions: SigningAction[] | null = null;
  selectedUser: GlobalSearchResult | null = null;

  constructor(
    @Inject(MAT_DIALOG_DATA) public readonly data: GlobalSearchResponse,
    private readonly dialogRef: MatDialogRef<GlobalSearchResultDialogComponent>,
    private readonly dialog: MatDialog,
    private readonly router: Router,
    private readonly store: Store<State>,
    private readonly tagService: TagService,
    private readonly punchService: PunchService,
  ) {}

  get resultCount(): number {
    return this.data.tags.length + this.data.checksheets.length + this.data.punches.length + this.data.users.length;
  }

  openChecksheet(result: GlobalSearchResult): void {
    if (!result.csaId || !result.cpId) return;
    const route =
      result.checkSheetType === 'MC' && result.mcpId
        ? `/cp/${result.cpId}/mcp/${result.mcpId}/checksheet/${result.csaId}`
        : `/cp/${result.cpId}/checksheet/${result.csaId}`;
    this.router.navigate([route]);
    this.close();
  }

  openCp(result: GlobalSearchResult): void {
    if (!result.cpId) return;
    this.router.navigate([`/cp/${result.cpId}`]);
    this.close();
  }

  openMcPackage(result: GlobalSearchResult): void {
    if (!result.cpId || !result.mcpId) return;
    this.router.navigate([`/cp/${result.cpId}/mcp/${result.mcpId}`]);
    this.close();
  }

  openPunch(result: GlobalSearchResult): void {
    if (!result.punchId) return;
    this.store
      .select(getCurrentProject)
      .pipe(take(1))
      .subscribe((project: Project | null) => {
        if (!project?.id) return;
        this.punchService
          .getPunchById(project.id, result.punchId)
          .pipe(take(1))
          .subscribe((punch) => {
            this.dialog.open(PunchDialogComponent, {
              data: { punch: { ...punch }, editMode: PunchCreateEditMode.EditPunch },
              panelClass: 'punch-dialog-panel',
              maxHeight: '92vh',
            });
          });
      });
  }

  showSigningActions(user: GlobalSearchResult): void {
    if (!user.userId) return;
    this.selectedUser = user;
    this.signingActions = null;
    this.store
      .select(getCurrentProject)
      .pipe(take(1))
      .subscribe((project: Project | null) => {
        if (!project?.id) return;
        this.tagService
          .getLatestSigningActions(project.id, user.userId)
          .pipe(take(1))
          .subscribe((actions) => (this.signingActions = actions));
      });
  }

  openAction(action: SigningAction): void {
    this.openChecksheet({
      csaId: action.csaId,
      cpId: action.cpId,
      mcpId: action.mcpId,
      checkSheetType: action.checkSheetType,
    } as GlobalSearchResult);
  }

  close(): void {
    this.dialogRef.close();
  }
}
